import { skipToken } from "@reduxjs/toolkit/query";
import type { DateBasis } from "@repo/app-types/entities/dateBasis";
import type { DateRange } from "@repo/app-types/entities/dateRange";
import {
  FIRST_ACTIVITY_DATE_LABEL,
  getCreatedAtBasis,
  getCreatedAtBasisLabel,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import { DateRangeDialog } from "@repo/ui/dateRange/dateRangeDialog";
import { formatDateRangeCondition } from "@repo/ui/dateRange/formatDateRange";
import { Size } from "@repo/ui/helpers/helpers";
import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuItem } from "@repo/ui/Menu/simpleMenuItem";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import { popoverStyle } from "@repo/ui/popover/style";
import { selectStyle } from "@repo/ui/Select/style";
import { IconSelector } from "@tabler/icons-react";
import { useContext, useMemo, type FC } from "react";
import {
  Button,
  DialogTrigger,
  Popover,
  type Selection,
} from "react-aria-components";
import { useCreatedAtBasisFieldNamesQuery } from "../../api/entities/customFieldName.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { FilterContext } from "./filterContext.js";

const chipStyle = selectStyle({ size: Size.SMALL });

const LAST_ACTIVITY_LABEL = "Most recent call";

/** Stand-in while a custom-field basis's name is still loading. */
const CREATION_DATE_LABEL = "Creation date";

export interface DateRangeFilterItemProps {
  playbookId?: PlaybookId;
  /**
   * Whether the page opted into `dateBasis`. A page that did not send the basis
   * would show a switcher that changes the chip and nothing else, so it gets
   * the range chip alone.
   */
  hasBasisPicker: boolean;
  value: DateRange;
  onChange: (newValue: DateRange) => void;
}

/**
 * The date filter is two joined chips: which date the range applies to, then
 * the range itself. The basis has no chip of its own, so it is read and written
 * straight from the filter context rather than being threaded through every
 * page's `useFilters` call.
 */
export const DateRangeFilterItem: FC<DateRangeFilterItemProps> = (props) => {
  const { values, onChange } = useContext(FilterContext);

  if (!props.hasBasisPicker) {
    return <DateRangePicker value={props.value} onChange={props.onChange} />;
  }

  return (
    // `isolate` so the focus ring's z-10 below competes only with its sibling
    // chip rather than whatever stacking context happens to be nearest. It
    // creates a context without clipping, and both popovers portal to the body,
    // so neither gets trapped underneath.
    <div className="isolate flex min-w-0 items-center">
      <DateBasisPicker
        playbookId={props.playbookId}
        value={values.dateBasis}
        onChange={(dateBasis) => {
          onChange({ dateBasis });
        }}
      />
      <DateRangePicker
        className="-ml-px rounded-l-none focus-visible:z-10 data-[focus-visible]:z-10"
        value={props.value}
        onChange={props.onChange}
      />
    </div>
  );
};

interface DateBasisPickerProps {
  playbookId?: PlaybookId;
  value: DateBasis;
  onChange: (newValue: DateBasis) => void;
}

const DateBasisPicker: FC<DateBasisPickerProps> = (props) => {
  const { data: playbook } = usePlaybookByIdQuery(
    props.playbookId ?? skipToken,
  );
  const basis = getCreatedAtBasis(playbook);
  // Only fetched when it can change the answer: a first-activity playbook — the
  // default, and most of them — names its creation date without any field.
  const { data: fieldNames } = useCreatedAtBasisFieldNamesQuery(
    basis.type === "custom_field" ? undefined : skipToken,
  );

  // The creation-date option is named after whatever the playbook reports
  // interactions under, so it reads as the customer's own term for it. Until
  // the names arrive there is no honest specific label, and the first-activity
  // one would be a lie for a custom-field basis, so it stays generic.
  const creationDateLabel = useMemo(() => {
    if (basis.type === "first_activity") {
      return FIRST_ACTIVITY_DATE_LABEL;
    }
    if (!fieldNames) {
      return CREATION_DATE_LABEL;
    }
    return getCreatedAtBasisLabel(
      basis,
      new Map(fieldNames.map((field) => [field.id, field.display_name])),
    );
  }, [basis, fieldNames]);

  const handleSelectionChange = (keys: Selection) => {
    if (keys === "all") {
      return;
    }
    const [selected] = [...keys];
    if (selected === "last_activity" || selected === "creation_date") {
      props.onChange(selected);
    }
  };

  return (
    <DialogTrigger>
      <Button className={chipStyle.button({ className: "rounded-r-none" })}>
        <span className={chipStyle.value({ className: "max-w-44" })}>
          {props.value === "creation_date"
            ? creationDateLabel
            : LAST_ACTIVITY_LABEL}
        </span>
        <IconSelector className={chipStyle.chevron()} />
      </Button>

      <SimpleMenuPopover placement="bottom start">
        <SimpleMenu
          aria-label="Choose which date to filter on"
          disallowEmptySelection
          selectionMode="single"
          selectedKeys={[props.value]}
          onSelectionChange={handleSelectionChange}
        >
          <SimpleMenuItem id="last_activity">
            {LAST_ACTIVITY_LABEL}
          </SimpleMenuItem>
          <SimpleMenuItem id="creation_date">
            {creationDateLabel}
          </SimpleMenuItem>
        </SimpleMenu>
      </SimpleMenuPopover>
    </DialogTrigger>
  );
};

interface DateRangePickerProps {
  value: DateRange;
  onChange: (newValue: DateRange) => void;
  className?: string;
}

const DateRangePicker: FC<DateRangePickerProps> = (props) => {
  return (
    <DialogTrigger>
      <Button className={chipStyle.button({ className: props.className })}>
        <span className={chipStyle.value()}>
          {formatDateRangeCondition(props.value)}
        </span>
        <IconSelector className={chipStyle.chevron()} />
      </Button>

      <Popover
        placement="bottom start"
        className={popoverStyle().popover({
          className: "min-w-0",
        })}
      >
        <DateRangeDialog value={props.value} onChange={props.onChange} />
      </Popover>
    </DialogTrigger>
  );
};
